import type { Enemy } from "../entities/Enemy.ts";
import type { Firewall } from "../entities/Firewall.ts";
import type { EventBus } from "../core/EventBus.ts";
import { isStealthHidden } from "./StealthGuard.ts";

export interface FirewallCombatContext {
  firewalls: Firewall[];
  enemies: Enemy[];
  firewallSlowActive: boolean;
  firewallSlowFactor: number;
  firewallSlowRange: number;
  bus: EventBus;
}

/** Apply proximity slow from firewalls to nearby enemies */
export function applyFirewallSlow(ctx: FirewallCombatContext): void {
  if (!ctx.firewallSlowActive || ctx.firewalls.length === 0) return;
  const rangeSq = ctx.firewallSlowRange * ctx.firewallSlowRange;
  for (const enemy of ctx.enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    for (const fw of ctx.firewalls) {
      const dx = enemy.x - fw.x;
      const dy = enemy.y - fw.y;
      if (dx * dx + dy * dy <= rangeSq) {
        enemy.applySlow(ctx.firewallSlowFactor, 100);
        break;
      }
    }
  }
}

/** CR-21/22: Breaker enemies damage adjacent firewalls */
export function updateFirewallDamage(
  dt: number,
  ctx: FirewallCombatContext,
): void {
  if (ctx.firewalls.length === 0) return;
  const adjacentRange = 60; // ~1.25 cells
  const adjacentRangeSq = adjacentRange * adjacentRange;
  for (const enemy of ctx.enemies) {
    if (!enemy.active || enemy.firewallDamage <= 0) continue;
    for (const fw of ctx.firewalls) {
      if (!fw.alive) continue;
      const dx = enemy.x - fw.x;
      const dy = enemy.y - fw.y;
      if (dx * dx + dy * dy <= adjacentRangeSq) {
        const reflected = fw.takeDamage(enemy.firewallDamage * (dt / 1000));
        if (reflected > 0) {
          enemy.takeDamage(reflected);
        }
        if (!fw.alive) {
          ctx.bus.emit("firewall.destroyed", { firewall: fw });
        }
      }
    }
  }
}
